import saveAs from "file-saver";
import { createAxiosInstance } from "./ServicesHelpers";
import config from "../config.json";
import { Params } from "react-router-dom";
import { IPageable } from "../types/table/interfaces.type";
import { useQuery, UseQueryResult } from "@tanstack/react-query";
export interface IErrors {
  id: string;
  rowNumber: string;
  createdBy: number;
  createdDate: Date;
  errorMessage: string;
}
function parseFilename(disposition: string): string {
  const regex = /filename\*?=(?:UTF-8'')?['"]?([^;'"]+)['"]?/i;
  const match = regex.exec(disposition);
  if (match && match[1]) {
    try {
      return decodeURIComponent(match[1]);
    } catch {
      return match[1];
    }
  }

  const parts = disposition.split(";");
  const filePart =
    parts.find((part) => part.trim().toLowerCase().startsWith("filename=")) ||
    "";
  const fallback = filePart
    .trim()
    .replace(/^filename=/i, "")
    .replace(/['"]/g, "");
  return fallback;
}

const axiosInstance = createAxiosInstance(config.ADMIN_API_BASE_URL);

export const ExportAttachmentApi = async (id: string, type: string) => {
  try {
    const response = await axiosInstance.get(`/attachment/${id}/export`, {
      responseType: "blob",
    });
    const disp =
      response.headers["content-disposition"] ||
      response.headers["Content-Disposition"];
    let filename = "";
    if (disp) {
      filename = parseFilename(disp);
    }
    const blob = new Blob([response.data], {
      type: response.headers["content-type"] || "application/octet-stream",
    });

    saveAs(blob, filename);
    return response.data;
  } catch (error: any) {
    console.error(
      "Error fetching file: " + id,
      error.response?.status,
      error.response?.data,
    );
  }
};

export const ExportErrorApi = async (id: string): Promise<Blob | void> => {
  try {
    const response = await axiosInstance.get(`/import-errors/csv`, {
      params: { importedFileId: id },
      responseType: "blob",
    });
    const disp =
      response.headers["content-disposition"] ||
      response.headers["Content-Disposition"];
    let filename = ``;
    if (disp) {
      const parsed = parseFilename(disp);
      if (parsed) {
        filename = parsed;
      }
    }
    const blob = new Blob([response.data], {
      type: response.headers["content-type"] || "text/csv",
    });
    saveAs(blob, filename);
    return blob;
  } catch (error: any) {
    console.error(
      "Error downloading import errors CSV:",
      error.response?.status,
      error.response?.data || error.message,
    );
    throw error;
  }
};

export async function fetchImportErrors(params: {
  sort?: string;
  page?: number;
  size?: number;
  importedFileId?: string;
}): Promise<IPageable<IErrors>> {
  try {
    const finalParams = {
      ...params,
      sort: params.sort || "rowNumber,asc",
    };
    console.log("Fetching import errors with params:", finalParams);
    const response = await axiosInstance.get("/import-errors", {
      params: finalParams,
    });
    return response.data;
  } catch (error: any) {
    console.error("Error fetching import errors:", error);
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while fetching import errors.",
    );
  }
}

export const useImportErrorsQuery = (params: {
  sort?: string;
  page?: number;
  size?: number;
  importedFileId?: string;
}): UseQueryResult<IPageable<IErrors>, Error> => {
  const queryKey = ["importErrors", JSON.stringify(params)];
  return useQuery({
    queryKey,
    queryFn: () => fetchImportErrors(params),
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
};
